<template>
  <div class="CardSatelliteImage">
    <div v-if="addTransparentLayer" class="transparent-layer" />
    <div
      class="MapRef"
      ref="googleMap"
    />
  </div>
</template>

<script>
import GoogleMapsApiLoader from 'google-maps-api-loader'
import _isEqual from 'lodash/isEqual'
import _chunk from 'lodash/chunk'
import { MIDDLEWARE_PATHS, AUDITS } from '../../../constants/const'
import {auth} from '../../../plugins/firebase'
import { getBearing } from '../../../lib/helpers'
import computeDestinationPoint from 'geolib/es/computeDestinationPoint'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { HeatmapLayer } from '@deck.gl/aggregation-layers'


const { V1, ALTIMETRY } = MIDDLEWARE_PATHS

const getLocationArray = (bounds, zoom) => {
  let matrix = []
  let stepLat
  let stepLng

  if (zoom >= 16) {
    stepLat = 0.0005000
    stepLng = 0.0005000
  } else if (zoom >= 15) {
    stepLat = 0.0010000
    stepLng = 0.0010000
  } else if (zoom >= 14) {
    stepLat = 0.0020000
    stepLng = 0.0020000
  } else {
    stepLat = 0.0030000
    stepLng = 0.0030000
  }
  let minLat = bounds.getSouthWest().lat()
  let maxLat = bounds.getNorthEast().lat() + stepLat
  let minLng = bounds.getSouthWest().lng()
  let maxLng = bounds.getNorthEast().lng() + stepLng

  for (var i = minLat; i <= maxLat; i += stepLat) {
    for (var j = minLng; j <= maxLng; j += stepLng) {
      matrix.push({
        lat: i,
        lng: j
      })
    }
  }
  return {
    points: matrix
  }

}

const getAuditLocationPoints = async (location, equipLength) => {
  const points = []
  let distanceToCenterPoints = 0
  let anglePoints = 0

  for (let i = 0; i < 360; i += AUDITS.ALTIMETRY.ANGLES_DIFFERENCE) {
    anglePoints++
    for (let j = 0; j < equipLength; j += AUDITS.ALTIMETRY.DISTANCE_TO_CENTER_DIFFERENCE) {
      if (anglePoints === 1) distanceToCenterPoints++
      const coordinates = computeDestinationPoint(
        { latitude: location.latitude, longitude: location.longitude },
        j,
        i
      )
      points.push({
        lat: coordinates.latitude,
        lng: coordinates.longitude
      })
    }
  }

  const authToken = await auth.currentUser.getIdToken()
  const response = await fetch(`${process.env.MIDDLEWARE_URL}${V1}${ALTIMETRY}`, {
    method: 'post',
    body: JSON.stringify({
      coordinates: points
    }),
    headers: {
      'Content-Type': 'application/json',
      'auth-token': authToken
    }
  })
  const data = await response.json()

  let results = []

  if (data.status === 'OK') {
    results = data.results
  }

  return {
    results,
    points,
    distanceToCenterPoints,
    anglePoints
  }
}

const toRad = (val) => {
  return val * Math.PI / 180
}

const toDeg = (val) => {
  return val * 180 / Math.PI
}

const getPointLatLng = (x, y, zoom, centerLat, centerLng, mapWidth, mapHeight) => {
  const parallelMultiplier = Math.cos(centerLat * Math.PI / 180)
  const degreesPerPixelX = 360 / Math.pow(2, zoom + 8)
  const degreesPerPixelY = 360 / Math.pow(2, zoom + 8) * parallelMultiplier
  const pointLat = centerLat - degreesPerPixelY * (y - mapHeight / 2)
  const pointLng = centerLng + degreesPerPixelX * (x - mapWidth / 2)
  return {
    lat: pointLat,
    lng: pointLng
  }
}

const getPointXY = (lat, lng, mapWidth, mapHeight, zoom, centerLat, centerLng) => {
  const upperLeftCornerCoordinates = getPointLatLng(0, 0, zoom, centerLat, centerLng, mapWidth, mapHeight)
  const lowerRightCornerCoordinates = getPointLatLng(mapWidth, mapHeight, zoom, centerLat, centerLng, mapWidth, mapHeight)
  const x = ((lng - upperLeftCornerCoordinates.lng) * mapWidth / (lowerRightCornerCoordinates.lng - upperLeftCornerCoordinates.lng))
  const y = ((upperLeftCornerCoordinates.lat - lat) * mapHeight / (upperLeftCornerCoordinates.lat - lowerRightCornerCoordinates.lat))
  return {
    x: x,
    y: y
  }
}

/**
 * Calculate the distance between two geographic points
 * @param {Object} point1 - {lat, lng}
 * @param {Object} point2 - {lat, lng}
 * @returns {Number} Distance in degrees (squared for performance)
 */
const calculateDistance = (point1, point2) => {
  const latDiff = point1.lat - point2.lat
  const lngDiff = point1.lng - point2.lng
  return latDiff * latDiff + lngDiff * lngDiff
}

/**
 * Find the altimetry data for the center location.
 * If exact match is not found, returns the closest one.
 * @param {Array} results - Array of altimetry results
 * @param {Object} center - {lat, lng} center coordinates
 * @returns {Object|null} Altimetry data for center or closest point
 */
const findCenterAltimetry = (results, center) => {
  // First, try to find exact match
  const exactMatch = results.find((r) => _isEqual(r.location, center))
  if (exactMatch) {
    return exactMatch
  }

  // If no exact match, find the closest point
  if (results.length === 0) {
    return null
  }

  let closest = results[0]
  let minDistance = calculateDistance(center, results[0].location)

  for (let i = 1; i < results.length; i++) {
    const distance = calculateDistance(center, results[i].location)
    if (distance < minDistance) {
      minDistance = distance
      closest = results[i]
    }
  }

  return closest
}

export default {
  data () {
    return {
      google: null,
      mapSize: 444,
      sideDistance: 0,
      circleBounds: null,
      resizeListener: null,
      resizeTimeout: null
    }
  },
  props: {
    position: Object,
    zoom: Number,
    equipLength: Number,
    heatmapData: {
      type: Array,
      default: () => null
    },
    hasHeatmap: Boolean,
    addTransparentLayer: Boolean,
    isAuditPivot: Boolean,
    mapInstanceId: String
  },
  created() {
    this.map = null
    this.deckOverlay = null
    this.localHeatmapLayer = null
  },
  async mounted () {
    const googleMapApi = await GoogleMapsApiLoader({
      apiKey: process.env.VUE_APP_GOOGLE_MAPS_KEY,
      libraries: []
    })

    // Function to be used later in map sizing
    googleMapApi.maps.LatLng.prototype.destinationPoint = function (brng, dist) {
      dist = dist / 6371071
      brng = toRad(brng)

      const lat1 = toRad(this.lat())
      const lon1 = toRad(this.lng())

      const lat2 = Math.asin(Math.sin(lat1) * Math.cos(dist) +
                            Math.cos(lat1) * Math.sin(dist) * Math.cos(brng))

      const lon2 = lon1 + Math.atan2(Math.sin(brng) * Math.sin(dist) *
                                    Math.cos(lat1),
                                    Math.cos(dist) - Math.sin(lat1) *
                                    Math.sin(lat2))

      if (isNaN(lat2) || isNaN(lon2)) return null

      return new googleMapApi.maps.LatLng(toDeg(lat2), toDeg(lon2))
    }

    this.google = googleMapApi

    this.initializeMap()
    
    // Add resize listener to recalculate zoom when map size changes
    this.resizeListener = this.handleMapResizeDebounced.bind(this)
    window.addEventListener('resize', this.resizeListener)
  },
  methods: {
    initializeMap () {
      if (this.deckOverlay) {
        this.deckOverlay.finalize()
        this.deckOverlay = null
      }
      const {latitude, longitude} = this.position
      const center = {lat: latitude, lng: longitude}
      const mapContainer = this.$refs.googleMap
      const radius = this.equipLength

      this.circleBounds = new this.google.maps.Circle({ center, radius }).getBounds()
      const preciseZoom = this.getBoundsZoomLevel(this.circleBounds, {
        width: mapContainer.clientWidth, 
        height: mapContainer.clientHeight
      })

      const map = new this.google.maps.Map(mapContainer, {
        center: center,
        zoom: preciseZoom,
        mapTypeId: 'satellite',
        mapTypeControl: false,
        draggable: false,
        scaleControl: false,
        scrollwheel: false,
        navigationControl: false,
        streetViewControl: false,
        disableDefaultUI: true,
        cursor: 'default',
        isFractionalZoomEnabled: true
      })

      // Getting distance of map's left side
      this.google.maps.event.addListenerOnce(map, 'projection_changed', () => {
        const centerLocation = new this.google.maps.LatLng(center)
        const radLocation = centerLocation.destinationPoint(270, radius)

        const xyRadius = getPointXY(radLocation.lat(), radLocation.lng(), this.mapSize, this.mapSize, this.zoom, center.lat, center.lng)
        this.sideDistance = Math.round(xyRadius.x)
      })

      this.deckOverlay = new GoogleMapsOverlay({
        layers: []
      })
      this.deckOverlay.setMap(map)

      this.map = map
      this.setHeatmap(map)
    },
    async setHeatmap () {
      const {latitude, longitude} = this.position
      const center = {lat: latitude, lng: longitude}
      const radius = this.equipLength

      if (this.heatmapData) {
        this.updateLocalLayers()
        return
      }

      // Use circleBounds that was already calculated in initializeMap
      let bounds = this.circleBounds

      // If circleBounds is not available, calculate it
      if (!bounds) {
        const circle = new this.google.maps.Circle({ center, radius })
        bounds = circle.getBounds()
      }

      const zoom = this.map.getZoom() || this.zoom
      let locationArray = getLocationArray(bounds, zoom)
      locationArray.points.push(center)

      const emitData = (heatmap, elevationReference) => {
        this.$emit('update:heatmapData', heatmap)
        this.$emit('update:elevationReference', elevationReference)
      }

      try {
        const authToken = await auth.currentUser.getIdToken()
        const response = await fetch(`${process.env.MIDDLEWARE_URL}${V1}${ALTIMETRY}`, {
          method: 'post',
          body: JSON.stringify({
            coordinates: locationArray.points
          }),
          headers: {
            'Content-Type': 'application/json',
            'auth-token': authToken
          }
        })
        const data = await response.json()

        if (data.status === 'OK') {
          const { results } = data

          let auditAltimetryData
          let auditAltimetryPoints = []
          if (this.isAuditPivot) {
            let auditLocationPointsData = await getAuditLocationPoints(this.position, this.equipLength)
            auditAltimetryPoints = auditLocationPointsData.results

            const auditAltimetrySeries = _chunk(auditAltimetryPoints, auditLocationPointsData.distanceToCenterPoints)
            auditAltimetryData = {
              series: auditAltimetrySeries,
              distanceToCenterPoints: auditLocationPointsData.distanceToCenterPoints,
              anglePoints: auditLocationPointsData.anglePoints
            }
          }

          const centerAltimetry = findCenterAltimetry(results, center)

          // Calculate minValue from results only (since rawData uses results)
          // This ensures weight is always >= 0
          let minValue = results.reduce((min, current) => {
            if (!current || current.elevation == null) return min
            if (!min || min.elevation == null) return current
            return current.elevation < min.elevation ? current : min
          })
          
          // Calculate maxValue from combined data for display purposes
          let maxValue = (this.isAuditPivot ? auditAltimetryPoints : results).reduce((max, current) => {
            if (!current || current.elevation == null) return max
            if (!max || max.elevation == null) return current
            return current.elevation > max.elevation ? current : max
          })

          // Validate minValue and maxValue exist
          if (!minValue || minValue.elevation == null) {
            minValue = { elevation: 0 }
          }
          if (!maxValue || maxValue.elevation == null) {
            maxValue = { elevation: minValue.elevation }
          }

          const METERS_PER_DEGREE = 111000
          const elevationRange = maxValue.elevation - minValue.elevation

          // Calculate weights with proper normalization
          // Normalize to 0-1 range, then scale to ensure all points are visible
          // Add a small base value to ensure even minimum weights are visible
          const MIN_WEIGHT_VISIBILITY = 0.1 // Minimum weight to ensure visibility
          const rawData = results.map(r => {
            const elevation = r.elevation != null ? parseFloat(r.elevation) : minValue.elevation
            
            // Calculate normalized weight (0 to 1)
            let normalizedWeight = 0
            if (elevationRange > 0) {
              normalizedWeight = (elevation - minValue.elevation) / elevationRange
            } else {
              // If all elevations are the same, give all points the same weight
              normalizedWeight = 0.5
            }
            
            // Scale weight to ensure minimum visibility
            // This ensures even the minimum elevation has a visible weight
            const weight = MIN_WEIGHT_VISIBILITY + (normalizedWeight * (1 - MIN_WEIGHT_VISIBILITY))
            
            return {
              position: [parseFloat(r.location.lng), parseFloat(r.location.lat)], 
              weight: weight
            }
          }).filter(r => {
            if (isNaN(r.position[0]) || isNaN(r.position[1])) return false

            const dLat = Math.abs(r.position[1] - center.lat)
            const dLng = Math.abs(r.position[0] - center.lng)
            
            const distMeters = Math.sqrt(
              Math.pow(dLat * METERS_PER_DEGREE, 2) + 
              Math.pow(dLng * METERS_PER_DEGREE * Math.cos(center.lat * Math.PI / 180), 2)
            )

            if (distMeters > (radius * 1.05)) {
              return false
            }

            return true
          })

          const cleanData = Object.freeze(rawData)

          // Store data for later recreation of the layer
          emitData(cleanData, {
            min: minValue.elevation,
            max: maxValue.elevation,
            minAngle: getBearing({
              latitude: this.position.latitude,
              longitude: this.position.longitude
            }, {
              latitude: minValue.location ? minValue.location.lat : minValue.lat,
              longitude: minValue.location ? minValue.location.lng : minValue.lng
            }), maxAngle: getBearing({
              latitude: this.position.latitude,
              longitude: this.position.longitude
            }, {
              latitude: maxValue.location ? maxValue.location.lat : maxValue.lat,
              longitude: maxValue.location ? maxValue.location.lng : maxValue.lng
            }),
            centerElevation: centerAltimetry ? centerAltimetry.elevation : null,
            auditAltimetryData
          })
        }
      } catch (e) {
        console.log('ALTIMETRY_ERROR', e)
      }
    },
    updateLocalLayers() {
      if (this.deckOverlay) {
        if (this.heatmapData) {
          this.localHeatmapLayer = this.createHeatmapLayer()
          this.deckOverlay.setProps({ layers: [this.localHeatmapLayer] })
        } else {
          this.deckOverlay.setProps({layers: []})
        }
      }
    },
    getBoundsZoomLevel (bounds, mapDim) {
      const ne = bounds.getNorthEast()
      const sw = bounds.getSouthWest()

      const lngDiff = ne.lng() - sw.lng()

      const exactZoom = Math.log2(mapDim.width * 360 / (lngDiff * 256))
      
      return exactZoom
    },
    calculateRadiusPixels (zoom) {
      const baseZoom = 12
      const baseRadiusPixels = 30 // 30px radius at zoom 12

      // 2 to the power of zoom difference matches the map scaling
      const radius = baseRadiusPixels * Math.pow(1.7, zoom - baseZoom)
      
      // Clamp to prevent performance issues or covering the whole screen
      return Math.max(10, Math.min(400, radius))
    },
    getSafeWeight (d) {
      return (Number.isFinite(d.weight)) ? d.weight : 0
    },
    createHeatmapLayer (zoom) {
      if (!this.heatmapData) {
        return null
      }

      // Default values if not provided
      zoom = zoom || this.map ? this.map.getZoom() : this.zoom
      const radiusPixels = this.calculateRadiusPixels(zoom)

      return new HeatmapLayer({
        id: `${this.mapInstanceId}-altimetry-heatmap`,
        data: this.heatmapData,
        visible: this.hasHeatmap,
        getPosition: d => d.position,
        getWeight: this.getSafeWeight,
        threshold: 0.01, // Very low threshold to ensure all points are visible
        radiusPixels: radiusPixels,
        intensity: 1,
        aggregation: 'SUM',
        colorRange: [
          [108, 218, 25],
          [156, 210, 24],
          [204, 201, 22],
          [215, 119, 26],
          [226, 36, 30]
        ]
      })
    },
    handleMapResizeDebounced () {
      // Debounce resize handler to avoid excessive recalculations
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout)
      }
      this.resizeTimeout = setTimeout(() => {
        this.handleMapResize()
      }, 150)
    },
    handleMapResize () {
      if (!this.map || !this.circleBounds || !this.$refs.googleMap) {
        return
      }
      
      // Wait for DOM to update before getting dimensions
      this.$nextTick(() => {
        const mapContainer = this.$refs.googleMap
        if (!mapContainer) {
          return
        }
        
        const newWidth = mapContainer.clientWidth
        const newHeight = mapContainer.clientHeight

        // Only recalculate if size actually changed and is valid
        if (newWidth === 0 || newHeight === 0) {
          return
        }
        
        // Notify Google Maps that the container size has changed
        // This is critical for proper recalculation
        this.google.maps.event.trigger(this.map, 'resize')
        
        // Use a small delay to ensure map processes the resize
        setTimeout(() => {
          // Recalculate zoom based on new dimensions
          const preciseZoom = this.getBoundsZoomLevel(this.circleBounds, {
            width: newWidth,
            height: newHeight
          })
          
          // Get current center to maintain it
          const {latitude, longitude} = this.position
          const center = {lat: latitude, lng: longitude}
          
          // Update map zoom and center to ensure circleBounds is always visible
          // Using setCenter and setZoom instead of fitBounds to maintain exact center
          this.map.setCenter(center)
          this.map.setZoom(preciseZoom)
          
          // Update heatmap layer radiusPixels based on new zoom and size
          if (this.localHeatmapLayer && this.hasHeatmap && this.heatmapData) {
            this.localHeatmapLayer = this.createHeatmapLayer(preciseZoom)
            // Update the overlay with the new layer
            this.updateLocalLayers()
          }
        }, 100)
      })
    }
  },
  watch: {
    hasHeatmap () {
      this.updateLocalLayers()
    },
    heatmapData () {
      this.updateLocalLayers()
    }
  },
  beforeDestroy() {
    if (this.deckOverlay) {
      this.deckOverlay.finalize()
    }
    // Remove resize listener and clear timeout
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener)
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
  }
}
</script>


// WEBPACK FOOTER //
// SatelliteImage.vue?ead8d3c2